// ProfileUpdateModal.jsx
import React, { useState } from "react";
import Modal from "react-modal";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { IoPersonCircle, IoCloseOutline } from "react-icons/io5";
import { MdOutlineMailOutline } from "react-icons/md";
import { FiCamera, FiPhone } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { updateUserProfile } from "../../../redux/auth/action/userProfileActions";
import userImage from "../../../assets/images/user.png";

Modal.setAppElement("#root");

const ProfileUpdateModal = ({ isOpen, onRequestClose, initialValues }) => {
  const dispatch = useDispatch();
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfilePicture(file);
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleUpdate = (values, { setSubmitting }) => {
    const formData = new FormData();
    formData.append('full_name', values.full_name);
    formData.append('email', values.email);
    formData.append('phone_number', values.phone_number || '');
    
    if (profilePicture) {
      formData.append('profile_picture', profilePicture);
    }
    
    dispatch(updateUserProfile(formData));
    setSubmitting(false);
    onRequestClose();
  };

  const validationSchema = Yup.object().shape({
    full_name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .required("Full name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone_number: Yup.string()
      .matches(/^\+?[1-9]\d{1,14}$/, "Please enter a valid phone number")
      .min(10, "Phone number must be at least 10 digits")
      .max(15, "Phone number must be at most 15 digits"),
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="flex justify-center items-center p-4"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Update Profile</h2>
          <button 
            onClick={onRequestClose} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <IoCloseOutline className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <Formik
            initialValues={{
              full_name: initialValues.full_name || '',
              email: initialValues.email || '',
              phone_number: initialValues.phone_number || ''
            }}
            validationSchema={validationSchema}
            onSubmit={handleUpdate}
            enableReinitialize={true}
          >
            {({ isSubmitting, values, handleChange, handleBlur, errors, touched }) => (
              <Form className="space-y-6">
                {/* Profile Picture */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <img 
                      src={previewUrl || initialValues.profile_picture || userImage} 
                      alt="Profile" 
                      className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                    />
                    <label 
                      htmlFor="profile-picture" 
                      className="absolute bottom-0 right-0 bg-sunday_primary text-white p-2 rounded-full cursor-pointer hover:bg-sunday_secondary transition-colors shadow-lg"
                    >
                      <FiCamera size={16} />
                    </label>
                  </div>
                  <input
                    id="profile-picture"
                    name="profile_picture"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Click camera icon to upload new photo
                  </p>
                </div>

                {/* Full Name */}
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <div className="relative">
                    <IoPersonCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="full_name"
                      name="full_name"
                      type="text"
                      value={values.full_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your full name"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-sunday_primary focus:border-transparent outline-none transition-all ${
                        errors.full_name && touched.full_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.full_name && touched.full_name && (
                    <div className="text-red-500 text-sm mt-1">{errors.full_name}</div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <MdOutlineMailOutline className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your email"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-sunday_primary focus:border-transparent outline-none transition-all ${
                        errors.email && touched.email ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.email && touched.email && (
                    <div className="text-red-500 text-sm mt-1">{errors.email}</div>
                  )}
                </div>

                {/* Phone Number - Simple Input */}
                <div>
                  <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="phone_number"
                      name="phone_number"
                      type="tel"
                      value={values.phone_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="+44 123 456 7890"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-sunday_primary focus:border-transparent outline-none transition-all ${
                        errors.phone_number && touched.phone_number ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.phone_number && touched.phone_number && (
                    <div className="text-red-500 text-sm mt-1">{errors.phone_number}</div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Include country code (e.g., +44 for UK)
                  </p>
                </div>

                {/* Submit Button */}
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full py-3 bg-sunday_primary hover:bg-sunday_secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-sunday_primary focus:ring-offset-2"
                >
                  {isSubmitting ? 'Updating...' : 'Update Profile'}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
};

export default ProfileUpdateModal;
